import { useEffect, useRef, useState } from 'react';

interface AnimatedFlowerBackgroundProps {
  className?: string;
  /** When set, use only this theme (no cycling). E.g. theme="bubbles" for bubbles only. */
  theme?: 'flowers' | 'bubbles' | 'waves';
}

type ThemeType = 'flowers' | 'bubbles' | 'waves';

export function AnimatedFlowerBackground({ className = '', theme: fixedTheme }: AnimatedFlowerBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentTheme, setCurrentTheme] = useState<ThemeType>(fixedTheme ?? 'flowers');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas to full size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Shared Configuration
    const config = {
      // Match the body's bg-primary-cream + GlobalNav cream (#faf7ef) so the
      // canvas, body, and nav read as a single continuous backdrop.
      backgroundColor: '#faf7ef',
      lineColor: '#4a4a4a',
      lineWidth: 1.0,
      maxElements: 25,
      spawnInterval: 200,
      drawSpeed: 0.004,
      animSpeed: 0.003,
      baseLifetime: 5000,
      initialElementsNoDelay: 10,
      themeChangeDuration: 5000, // Change theme every 5 seconds
      fadeDuration: 1500 // 1.5 second fade transition
    };

    let flowers: Flower[] = [];
    let bubbles: Bubble[] = [];
    let butterflies: ButterflyElement[] = [];
    let waves: Wave[] = [];
    let nextFlowers: Flower[] = [];
    let nextBubbles: Bubble[] = [];
    let nextWaves: Wave[] = [];
    let animationRunning = true;
    let lastTime = Date.now();
    let themeTimer = 0;
    let currentOpacity = 1;
    let nextOpacity = 0;
    let isTransitioning = false;
    let nextTheme: ThemeType | null = null;

    // Flower class
    class Flower {
      x: number;
      y: number;
      type: string;
      targetScale: number;
      currentScale: number;
      rotation: number;
      progress: number;
      opacity: number;
      age: number;
      delay: number;
      lifetime: number;
      floatOffsetX: number;
      floatOffsetY: number;
      floatSpeedX: number;
      floatSpeedY: number;
      bloomProgress: number;
      color: string;

      constructor(x: number, y: number, type: string, scale: number, delay: number = 0) {
        this.x = x;
        this.y = y;
        this.type = type;
        this.targetScale = scale;
        this.currentScale = 0;
        this.rotation = Math.random() * Math.PI * 2;
        this.progress = 0;
        this.opacity = 0;
        this.age = 0;
        this.delay = delay;
        this.lifetime = config.baseLifetime + Math.random() * 2000;
        this.floatOffsetX = Math.random() * Math.PI * 2;
        this.floatOffsetY = Math.random() * Math.PI * 2;
        this.floatSpeedX = 0.0000008 + Math.random() * 0.0000012; // Almost imperceptible drift
        this.floatSpeedY = 0.000001 + Math.random() * 0.0000015; // Almost imperceptible drift
        this.bloomProgress = 0;

        // Assign a random color from green-friendly harmonious palette
        const harmoniousColors = [
          '#059669', // Emerald
          '#10b981', // Emerald light
          '#047857', // Emerald dark
          '#065f46', // Deep forest
          '#2E8B57', // Sea green
          '#228B22', // Forest green
          '#0891b2', // Teal
          '#14b8a6', // Teal bright
          '#0d9488', // Teal medium
          '#06b6d4', // Cyan
          '#22d3ee', // Cyan light
          '#3b82f6', // Blue
          '#60a5fa', // Sky blue
          '#8BC34A', // Light green
          '#CDDC39', // Lime
          '#FDD835', // Yellow (warm accent)
          '#FFB74D', // Soft orange (warm accent)
        ];
        this.color = harmoniousColors[Math.floor(Math.random() * harmoniousColors.length)];
      }

      update(deltaTime: number) {
        this.age += deltaTime;

        if (this.age < this.delay) return;

        const effectiveAge = this.age - this.delay;

        // Simplified bloom - just grow to size and stay
        if (this.bloomProgress < 1) {
          this.bloomProgress += config.animSpeed * 0.5; // Very slow, gentle bloom
          this.currentScale = this.targetScale * Math.min(1, this.bloomProgress);
          this.opacity = 0.3 + (0.5 * Math.min(1, this.bloomProgress));
        } else {
          this.currentScale = this.targetScale;
          this.opacity = 0.8;
        }

        // Gentle fade out at end of life
        if (effectiveAge > this.lifetime - 2000) {
          const fadeProgress = (this.lifetime - effectiveAge) / 2000;
          this.opacity = Math.max(0, fadeProgress * 0.8);
        }

        // Very slow drawing progress
        if (this.progress < 1) {
          this.progress += config.drawSpeed * 0.5;
        }

        // Almost imperceptible drift
        this.floatOffsetX += this.floatSpeedX * 0.5;
        this.floatOffsetY += this.floatSpeedY * 0.5;
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.currentScale === 0 || this.age < this.delay) return;

        // Only apply drift to non-radial flowers
        const floatX = this.type === 'powderPuff' ? 0 : Math.sin(this.floatOffsetX) * 12;
        const floatY = this.type === 'powderPuff' ? 0 : Math.sin(this.floatOffsetY) * 9;

        ctx.save();
        ctx.translate(this.x + floatX, this.y + floatY);
        ctx.rotate(this.rotation);
        ctx.scale(this.currentScale, this.currentScale);
        ctx.globalAlpha = this.opacity * 0.7; // Reduced opacity for smoother appearance
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Use flower's assigned harmonious color
        ctx.strokeStyle = this.color;
        ctx.lineWidth = config.lineWidth / this.currentScale;

        (this as any)[this.type]();

        ctx.restore();
      }

      tulip() {
        const petals = 6;
        for (let i = 0; i < petals; i++) {
          const angle = (Math.PI * 2 / petals) * i;
          ctx!.save();
          ctx!.rotate(angle);
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(-15, -20, -15, -40, 0, -50);
          ctx!.bezierCurveTo(15, -40, 15, -20, 0, 0);
          if (this.progress >= i / petals) ctx!.stroke();
          ctx!.restore();
        }
        if (this.progress > 0.8) {
          ctx!.beginPath();
          ctx!.arc(0, 0, 5, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      radialDaisy() {
        const petals = 12 + Math.floor(Math.random() * 8);
        const innerRadius = 8;
        const outerRadius = 35 + Math.random() * 15;

        for (let i = 0; i < petals; i++) {
          if (this.progress >= i / petals) {
            const angle = (Math.PI * 2 / petals) * i;
            const x1 = Math.cos(angle) * innerRadius;
            const y1 = Math.sin(angle) * innerRadius;
            const x2 = Math.cos(angle) * outerRadius;
            const y2 = Math.sin(angle) * outerRadius;

            ctx!.beginPath();
            ctx!.moveTo(x1, y1);
            const cpx = Math.cos(angle) * (outerRadius * 0.7);
            const cpy = Math.sin(angle) * (outerRadius * 0.7);
            const perpAngle = angle + Math.PI / 2;
            const cpx2 = cpx + Math.cos(perpAngle) * 5;
            const cpy2 = cpy + Math.sin(perpAngle) * 5;
            ctx!.quadraticCurveTo(cpx2, cpy2, x2, y2);
            ctx!.stroke();
          }
        }

        if (this.progress > 0.7) {
          ctx!.beginPath();
          ctx!.arc(0, 0, innerRadius, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      hibiscus() {
        const petals = 5;
        const petalLength = 40;

        for (let i = 0; i < petals; i++) {
          if (this.progress >= i / petals) {
            const angle = (Math.PI * 2 / petals) * i - Math.PI / 2;
            ctx!.save();
            ctx!.rotate(angle);
            ctx!.beginPath();
            ctx!.moveTo(0, 0);
            ctx!.bezierCurveTo(-25, -10, -25, -30, -10, -petalLength);
            ctx!.bezierCurveTo(0, -petalLength - 5, 10, -petalLength, 10, -petalLength);
            ctx!.bezierCurveTo(25, -30, 25, -10, 0, 0);
            ctx!.stroke();
            ctx!.restore();
          }
        }
      }

      spikyChrysanthemum() {
        const spikes = 20 + Math.floor(Math.random() * 15);
        const baseRadius = 10;
        const spikeLength = 30 + Math.random() * 20;

        for (let i = 0; i < spikes; i++) {
          const angle = (Math.PI * 2 / spikes) * i;
          const curve = Math.sin(i * 0.5) * 0.3;
          ctx!.save();
          ctx!.rotate(angle);
          ctx!.beginPath();
          ctx!.moveTo(0, baseRadius);
          ctx!.bezierCurveTo(
            -5 + curve * 10, baseRadius + spikeLength * 0.3,
            5 + curve * 10, baseRadius + spikeLength * 0.6,
            0, baseRadius + spikeLength
          );
          ctx!.stroke();
          ctx!.restore();
        }
      }

      powderPuff() {
        const stamens = 30 + Math.floor(Math.random() * 20);
        const length = 35 + Math.random() * 15;

        for (let i = 0; i < stamens; i++) {
          const angle = (Math.PI * 2 / stamens) * i;
          const wobble = Math.sin(i * 0.7) * 5;
          const x = Math.cos(angle) * (length + wobble);
          const y = Math.sin(angle) * (length + wobble);
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.lineTo(x, y);
          ctx!.stroke();
          ctx!.beginPath();
          ctx!.arc(x, y, 1.5, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      bleedingHeart() {
        if (this.progress > 0) {
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(-20, -15, -20, -30, -10, -35);
          ctx!.bezierCurveTo(0, -40, 0, -30, 0, -20);
          ctx!.stroke();
        }
        if (this.progress > 0.3) {
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(20, -15, 20, -30, 10, -35);
          ctx!.bezierCurveTo(0, -40, 0, -30, 0, -20);
          ctx!.stroke();
        }
        if (this.progress > 0.6) {
          ctx!.beginPath();
          ctx!.moveTo(0, -20);
          ctx!.bezierCurveTo(-5, -10, -5, 0, 0, 10);
          ctx!.bezierCurveTo(5, 0, 5, -10, 0, -20);
          ctx!.stroke();
        }
      }

      borderedPetal() {
        const petals = 5;
        const radius = 35;

        for (let i = 0; i < petals; i++) {
          if (this.progress >= i / petals) {
            const angle = (Math.PI * 2 / petals) * i - Math.PI / 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            ctx!.beginPath();
            ctx!.arc(x, y, 15, 0, Math.PI * 2);
            ctx!.stroke();
            ctx!.beginPath();
            ctx!.arc(x, y, 12, 0, Math.PI * 2);
            ctx!.stroke();
          }
        }

        if (this.progress > 0.8) {
          ctx!.beginPath();
          ctx!.arc(0, 0, 8, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      tropicalLeaf() {
        const length = 60;
        const width = 35;

        if (this.progress > 0) {
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(-width, -length * 0.3, -width * 0.7, -length * 0.7, 0, -length);
          ctx!.stroke();
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(width, -length * 0.3, width * 0.7, -length * 0.7, 0, -length);
          ctx!.stroke();
        }
        if (this.progress > 0.3) {
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.lineTo(0, -length);
          ctx!.stroke();
        }
      }

      spiralPattern() {
        const spirals = 8;
        const maxRadius = 40;

        for (let s = 0; s < spirals; s++) {
          const angleOffset = (Math.PI * 2 / spirals) * s;
          ctx!.beginPath();
          const points = 30;
          for (let i = 0; i <= points; i++) {
            const t = i / points;
            const angle = angleOffset + t * Math.PI * 2;
            const radius = t * maxRadius;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            if (i === 0) ctx!.moveTo(x, y);
            else ctx!.lineTo(x, y);
          }
          ctx!.stroke();
        }
      }

      bellFlower() {
        if (this.progress > 0) {
          ctx!.beginPath();
          ctx!.moveTo(0, -10);
          ctx!.bezierCurveTo(-15, -20, -20, -40, -12, -50);
          ctx!.stroke();
          ctx!.beginPath();
          ctx!.moveTo(0, -10);
          ctx!.bezierCurveTo(15, -20, 20, -40, 12, -50);
          ctx!.stroke();
        }
        if (this.progress > 0.4) {
          ctx!.beginPath();
          ctx!.arc(0, -48, 15, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      // Art Nouveau Swirl with leaves
      artNouveauSwirl() {
        const spiralTurns = 1.5;
        const maxRadius = 40;

        if (this.progress > 0) {
          // Main spiral
          ctx!.beginPath();
          const spiralPoints = 30;
          for (let i = 0; i <= spiralPoints; i++) {
            const t = i / spiralPoints;
            const angle = t * Math.PI * 2 * spiralTurns;
            const radius = t * maxRadius;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            if (i === 0) ctx!.moveTo(x, y);
            else ctx!.lineTo(x, y);
          }
          ctx!.stroke();
        }

        if (this.progress > 0.5) {
          // Small leaf at end
          ctx!.save();
          const endAngle = spiralTurns * Math.PI * 2;
          const endX = Math.cos(endAngle) * maxRadius;
          const endY = Math.sin(endAngle) * maxRadius;
          ctx!.translate(endX, endY);
          ctx!.rotate(endAngle);
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(-5, -8, -5, -15, 0, -20);
          ctx!.bezierCurveTo(5, -15, 5, -8, 0, 0);
          ctx!.stroke();
          ctx!.restore();
        }
      }

      // Symmetrical mandala flower
      mandalaFlower() {
        const sections = 4;

        for (let i = 0; i < sections; i++) {
          const angle = (Math.PI * 2 / sections) * i;
          ctx!.save();
          ctx!.rotate(angle);

          // Petal shape
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(-15, -10, -15, -25, -8, -35);
          ctx!.bezierCurveTo(0, -38, 8, -35, 8, -35);
          ctx!.bezierCurveTo(15, -25, 15, -10, 0, 0);
          ctx!.stroke();

          // Inner spiral decoration
          ctx!.beginPath();
          ctx!.arc(-10, -20, 5, 0, Math.PI * 1.5);
          ctx!.stroke();

          ctx!.beginPath();
          ctx!.arc(10, -20, 5, Math.PI * 0.5, Math.PI * 2);
          ctx!.stroke();

          ctx!.restore();
        }

        // Center circle
        ctx!.beginPath();
        ctx!.arc(0, 0, 6, 0, Math.PI * 2);
        ctx!.stroke();
      }

      // Simple rounded petal daisy
      roundedDaisy() {
        const petals = 6 + Math.floor(Math.random() * 3);
        const petalLength = 25;

        for (let i = 0; i < petals; i++) {
          if (this.progress >= i / petals) {
            const angle = (Math.PI * 2 / petals) * i;
            ctx!.save();
            ctx!.rotate(angle);

            ctx!.beginPath();
            ctx!.arc(0, -petalLength, petalLength * 0.4, Math.PI * 0.2, Math.PI * 0.8);
            ctx!.lineTo(0, 0);
            ctx!.stroke();

            ctx!.restore();
          }
        }

        // Center
        if (this.progress > 0.7) {
          ctx!.beginPath();
          ctx!.arc(0, 0, 8, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      // Heart-shaped petal flower
      heartPetalFlower() {
        const petals = 5;

        for (let i = 0; i < petals; i++) {
          if (this.progress >= i / petals) {
            const angle = (Math.PI * 2 / petals) * i - Math.PI / 2;
            ctx!.save();
            ctx!.rotate(angle);

            // Heart shape petal
            ctx!.beginPath();
            ctx!.moveTo(0, 0);
            ctx!.bezierCurveTo(-10, -15, -12, -25, -6, -30);
            ctx!.bezierCurveTo(0, -32, 0, -32, 0, -32);
            ctx!.bezierCurveTo(0, -32, 0, -32, 6, -30);
            ctx!.bezierCurveTo(12, -25, 10, -15, 0, 0);
            ctx!.stroke();

            ctx!.restore();
          }
        }

        if (this.progress > 0.8) {
          ctx!.beginPath();
          ctx!.arc(0, 0, 5, 0, Math.PI * 2);
          ctx!.stroke();
        }
      }

      // Acanthus leaf
      acanthusLeaf() {
        if (this.progress > 0) {
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          // Main curved stem
          ctx!.bezierCurveTo(-10, -20, -15, -40, -10, -60);
          ctx!.stroke();

          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.bezierCurveTo(10, -20, 15, -40, 10, -60);
          ctx!.stroke();
        }

        if (this.progress > 0.3) {
          // Curved side lobes
          const lobes = 3;
          for (let i = 0; i < lobes; i++) {
            const y = -15 - (i * 15);

            ctx!.beginPath();
            ctx!.moveTo(-10, y);
            ctx!.bezierCurveTo(-20, y - 5, -18, y - 10, -10, y - 12);
            ctx!.stroke();

            ctx!.beginPath();
            ctx!.moveTo(10, y);
            ctx!.bezierCurveTo(20, y - 5, 18, y - 10, 10, y - 12);
            ctx!.stroke();
          }
        }

        if (this.progress > 0.7) {
          // Tip curl
          ctx!.beginPath();
          ctx!.moveTo(-10, -60);
          ctx!.bezierCurveTo(-15, -70, -10, -75, 0, -75);
          ctx!.bezierCurveTo(10, -75, 15, -70, 10, -60);
          ctx!.stroke();
        }
      }

      // Cherry blossom branch
      cherryBlossom() {
        // Branch
        if (this.progress > 0) {
          ctx!.beginPath();
          ctx!.moveTo(-30, 0);
          ctx!.bezierCurveTo(-10, -10, 10, -15, 30, -10);
          ctx!.stroke();
        }

        // Blossoms along branch
        const blossoms = 4;
        for (let i = 0; i < blossoms; i++) {
          if (this.progress >= (0.3 + i * 0.15)) {
            const t = i / (blossoms - 1);
            const x = -30 + t * 60;
            const y = -Math.sin(t * Math.PI) * 15;

            ctx!.save();
            ctx!.translate(x, y);

            // Simple 5-petal blossom
            const petals = 5;
            for (let p = 0; p < petals; p++) {
              const angle = (Math.PI * 2 / petals) * p;
              ctx!.save();
              ctx!.rotate(angle);
              ctx!.beginPath();
              ctx!.moveTo(0, 0);
              ctx!.lineTo(0, -8);
              ctx!.stroke();
              ctx!.restore();
            }

            ctx!.beginPath();
            ctx!.arc(0, 0, 2, 0, Math.PI * 2);
            ctx!.stroke();

            ctx!.restore();
          }
        }
      }

      // Delicate branching
      delicateBranch() {
        if (this.progress > 0) {
          // Main stem
          ctx!.beginPath();
          ctx!.moveTo(0, 0);
          ctx!.lineTo(0, -50);
          ctx!.stroke();
        }

        if (this.progress > 0.3) {
          // Side branches
          const branches = 4;
          for (let i = 0; i < branches; i++) {
            const y = -10 - (i * 10);
            const side = i % 2 === 0 ? 1 : -1;
            const length = 15 + Math.random() * 10;

            ctx!.beginPath();
            ctx!.moveTo(0, y);
            ctx!.bezierCurveTo(
              side * 10, y - 5,
              side * length, y - length * 0.5,
              side * length, y - length
            );
            ctx!.stroke();

            // Tiny leaves
            if (this.progress > 0.6) {
              ctx!.beginPath();
              ctx!.moveTo(side * length, y - length);
              ctx!.bezierCurveTo(
                side * (length + 3), y - length - 2,
                side * (length + 3), y - length - 5,
                side * length, y - length - 7
              );
              ctx!.stroke();
            }
          }
        }
      }

      isAlive() {
        return this.age < (this.lifetime + this.delay);
      }
    }

    // Bubble class - gentle floating circles from all directions
    class Bubble {
      x: number;
      y: number;
      targetScale: number;
      currentScale: number;
      opacity: number;
      age: number;
      delay: number;
      lifetime: number;
      floatSpeedY: number;
      floatSpeedX: number;
      wobble: number;
      wobbleSpeed: number;
      color: string;
      radius: number;
      willPop: boolean;
      popProgress: number;
      isPopping: boolean;
      spawnEdge: string;

      constructor(x: number, y: number, scale: number, delay: number = 0, fromEdge?: string) {
        this.targetScale = scale;
        this.currentScale = 0;
        this.opacity = 0;
        this.age = 0;
        this.delay = delay;
        this.lifetime = config.baseLifetime + Math.random() * 3000;
        this.wobble = Math.random() * Math.PI * 2;
        this.wobbleSpeed = 0.02 + Math.random() * 0.02;
        this.radius = 15 + Math.random() * 25;
        this.willPop = Math.random() > 0.6; // 40% chance to pop
        this.popProgress = 0;
        this.isPopping = false;

        // Randomly choose edge if not specified
        const edges = ['top', 'right', 'bottom', 'left'];
        this.spawnEdge = fromEdge || edges[Math.floor(Math.random() * edges.length)];

        // Set position and movement based on spawn edge
        switch (this.spawnEdge) {
          case 'top':
            this.x = x || Math.random() * canvas!.width;
            this.y = -50;
            this.floatSpeedX = (Math.random() - 0.5) * 0.4;
            this.floatSpeedY = 0.2 + Math.random() * 0.3;
            break;
          case 'right':
            this.x = canvas!.width + 50;
            this.y = y || Math.random() * canvas!.height;
            this.floatSpeedX = -(0.2 + Math.random() * 0.3);
            this.floatSpeedY = (Math.random() - 0.5) * 0.4;
            break;
          case 'bottom':
            this.x = x || Math.random() * canvas!.width;
            this.y = canvas!.height + 50;
            this.floatSpeedX = (Math.random() - 0.5) * 0.4;
            this.floatSpeedY = -(0.2 + Math.random() * 0.3);
            break;
          case 'left':
            this.x = -50;
            this.y = y || Math.random() * canvas!.height;
            this.floatSpeedX = 0.2 + Math.random() * 0.3;
            this.floatSpeedY = (Math.random() - 0.5) * 0.4;
            break;
          default:
            this.x = x;
            this.y = y;
            this.floatSpeedX = (Math.random() - 0.5) * 0.3;
            this.floatSpeedY = -(0.2 + Math.random() * 0.3);
        }

        const harmoniousColors = [
          '#059669', '#10b981', '#047857', '#065f46',
          '#0891b2', '#14b8a6', '#0d9488', '#06b6d4',
          '#22d3ee', '#3b82f6', '#60a5fa', '#8BC34A'
        ];
        this.color = harmoniousColors[Math.floor(Math.random() * harmoniousColors.length)];
      }

      update(deltaTime: number) {
        this.age += deltaTime;
        if (this.age < this.delay) return;

        const effectiveAge = this.age - this.delay;

        // Check if should start popping (near end of life)
        if (this.willPop && !this.isPopping && effectiveAge > this.lifetime - 800) {
          this.isPopping = true;
          this.popProgress = 0;
        }

        if (this.isPopping) {
          // Pop animation: expand quickly then fade
          const popSpeed = 0.015;
          this.popProgress += popSpeed;

          if (this.popProgress < 1) {
            // Growing phase
            this.currentScale = this.targetScale * (1 + this.popProgress * 0.3);
            this.opacity = 0.8 - (this.popProgress * 0.5);
          } else {
            // Rapid fade after expansion
            this.opacity = Math.max(0, 0.3 - ((this.popProgress - 1) * 2));
          }
        } else {
          // Normal bubble behavior
          // Scale up gently
          if (this.currentScale < this.targetScale) {
            this.currentScale = Math.min(this.targetScale, this.currentScale + config.animSpeed);
          }

          // Fade in and out
          if (effectiveAge < 1000) {
            this.opacity = Math.min(0.8, effectiveAge / 1000 * 0.8);
          } else if (effectiveAge > this.lifetime - 1000 && !this.willPop) {
            this.opacity = Math.max(0, (this.lifetime - effectiveAge) / 1000 * 0.8);
          } else {
            this.opacity = 0.8;
          }
        }

        // Float upward with gentle wobble
        this.y += this.floatSpeedY;
        this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 0.3 + this.floatSpeedX;
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.currentScale === 0 || this.age < this.delay) return;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.currentScale, this.currentScale);
        ctx.globalAlpha = this.opacity * 0.7;

        ctx.strokeStyle = this.color;
        ctx.lineWidth = config.lineWidth / this.currentScale;
        ctx.lineCap = 'round';

        if (this.isPopping && this.popProgress > 0.8) {
          // Draw pop burst lines
          const burstLines = 6;
          const burstLength = this.radius * (this.popProgress - 0.8) * 3;
          for (let i = 0; i < burstLines; i++) {
            const angle = (Math.PI * 2 / burstLines) * i;
            ctx.beginPath();
            ctx.moveTo(
              Math.cos(angle) * this.radius,
              Math.sin(angle) * this.radius
            );
            ctx.lineTo(
              Math.cos(angle) * (this.radius + burstLength),
              Math.sin(angle) * (this.radius + burstLength)
            );
            ctx.stroke();
          }
        } else {
          // Simple single circle
          ctx.beginPath();
          ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
          ctx.stroke();
        }

        ctx.globalAlpha = 1;
        ctx.restore();
      }

      isAlive() {
        // Check if bubble is still on screen (with some margin)
        const onScreen = this.x > -100 && this.x < canvas!.width + 100 &&
                         this.y > -100 && this.y < canvas!.height + 100;
        return this.age < (this.lifetime + this.delay) && onScreen;
      }
    }

    // Butterfly class - simple geometric butterflies
    class ButterflyElement {
      x: number;
      y: number;
      targetScale: number;
      currentScale: number;
      opacity: number;
      age: number;
      delay: number;
      lifetime: number;
      directionX: number;
      directionY: number;
      speed: number;
      wingPhase: number;
      wingSpeed: number;
      color: string;
      flutterOffset: number;

      constructor(x: number, y: number, scale: number, delay: number = 0) {
        this.x = x;
        this.y = y;
        this.targetScale = scale;
        this.currentScale = 0;
        this.opacity = 0;
        this.age = 0;
        this.delay = delay;
        this.lifetime = config.baseLifetime + Math.random() * 4000;

        // Random flight direction
        const angle = Math.random() * Math.PI * 2;
        this.directionX = Math.cos(angle);
        this.directionY = Math.sin(angle);
        this.speed = 0.3 + Math.random() * 0.5;
        this.wingPhase = Math.random() * Math.PI * 2;
        this.wingSpeed = 0.1 + Math.random() * 0.05;
        this.flutterOffset = 0;

        const harmoniousColors = [
          '#059669', '#10b981', '#047857', '#0891b2',
          '#14b8a6', '#06b6d4', '#22d3ee', '#3b82f6',
          '#60a5fa', '#8BC34A', '#FDD835', '#FFB74D'
        ];
        this.color = harmoniousColors[Math.floor(Math.random() * harmoniousColors.length)];
      }

      update(deltaTime: number) {
        this.age += deltaTime;
        if (this.age < this.delay) return;

        const effectiveAge = this.age - this.delay;

        // Scale up
        if (this.currentScale < this.targetScale) {
          this.currentScale = Math.min(this.targetScale, this.currentScale + config.animSpeed);
        }

        // Fade in and out
        if (effectiveAge < 1000) {
          this.opacity = Math.min(0.8, effectiveAge / 1000 * 0.8);
        } else if (effectiveAge > this.lifetime - 1000) {
          this.opacity = Math.max(0, (this.lifetime - effectiveAge) / 1000 * 0.8);
        } else {
          this.opacity = 0.8;
        }

        // Flutter movement
        this.flutterOffset += this.wingSpeed;
        const flutter = Math.sin(this.flutterOffset) * 2;

        // Move in direction with flutter
        this.x += this.directionX * this.speed + flutter * 0.3;
        this.y += this.directionY * this.speed + Math.cos(this.flutterOffset) * 0.5;

        // Wing animation
        this.wingPhase += this.wingSpeed;

        // Bounce off edges
        if (this.x < 0 || this.x > canvas!.width) this.directionX *= -1;
        if (this.y < 0 || this.y > canvas!.height) this.directionY *= -1;
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.currentScale === 0 || this.age < this.delay) return;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.currentScale, this.currentScale);
        ctx.globalAlpha = this.opacity * 0.7;

        ctx.strokeStyle = this.color;
        ctx.lineWidth = config.lineWidth / this.currentScale;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const wingSpread = Math.abs(Math.sin(this.wingPhase)) * 0.6 + 0.5;

        // Simple body - vertical line
        ctx.beginPath();
        ctx.moveTo(0, -8);
        ctx.lineTo(0, 8);
        ctx.stroke();

        // Antennae
        ctx.beginPath();
        ctx.moveTo(0, -8);
        ctx.lineTo(-3, -12);
        ctx.moveTo(0, -8);
        ctx.lineTo(3, -12);
        ctx.stroke();

        // Simple wings - just curved lines
        ctx.save();
        ctx.scale(wingSpread, 1);

        // Left wing - simple arc
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(-20, -5, -25, -15);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(-18, 5, -20, 12);
        ctx.stroke();

        // Right wing - simple arc
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(20, -5, 25, -15);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(18, 5, 20, 12);
        ctx.stroke();

        ctx.restore();

        ctx.globalAlpha = 1;
        ctx.restore();
      }

      isAlive() {
        return this.age < (this.lifetime + this.delay);
      }
    }

    // Wave class - flowing wave patterns with trails
    class Wave {
      y: number;
      amplitude: number;
      amplitudeVariation: number;
      frequency: number;
      phase: number;
      phaseSpeed: number;
      opacity: number;
      age: number;
      delay: number;
      lifetime: number;
      color: string;
      direction: number;
      verticalDirection: number;
      verticalSpeed: number;
      hasTrail: boolean;
      trailPositions: Array<{ phase: number; opacity: number }>;

      constructor(y: number, delay: number = 0) {
        this.y = y;
        // Increased amplitude variation for more dramatic highs and lows
        this.amplitude = 20 + Math.random() * 45;
        this.amplitudeVariation = 0.3 + Math.random() * 0.7; // Variation along the wave
        this.frequency = 0.003 + Math.random() * 0.012;
        this.phase = Math.random() * Math.PI * 2;
        this.phaseSpeed = 0.008 + Math.random() * 0.025;
        this.opacity = 0;
        this.age = 0;
        this.delay = delay;
        this.lifetime = config.baseLifetime + Math.random() * 4000;
        this.direction = Math.random() > 0.5 ? 1 : -1; // Horizontal direction
        this.verticalDirection = (Math.random() - 0.5) * 0.3; // Gentle vertical drift
        this.verticalSpeed = 0.05 + Math.random() * 0.1;
        this.hasTrail = Math.random() > 0.5; // 50% have trails
        this.trailPositions = [];

        const harmoniousColors = [
          '#059669', '#10b981', '#047857', '#0891b2',
          '#14b8a6', '#0d9488', '#06b6d4', '#22d3ee',
          '#3b82f6', '#60a5fa'
        ];
        this.color = harmoniousColors[Math.floor(Math.random() * harmoniousColors.length)];
      }

      update(deltaTime: number) {
        this.age += deltaTime;
        if (this.age < this.delay) return;

        const effectiveAge = this.age - this.delay;

        // Fade in and out
        if (effectiveAge < 1000) {
          this.opacity = Math.min(0.6, effectiveAge / 1000 * 0.6);
        } else if (effectiveAge > this.lifetime - 1000) {
          this.opacity = Math.max(0, (this.lifetime - effectiveAge) / 1000 * 0.6);
        } else {
          this.opacity = 0.6;
        }

        // Save trail positions for waves with trails
        if (this.hasTrail) {
          this.trailPositions.push({
            phase: this.phase,
            opacity: this.opacity * 0.5
          });
          // Keep only last 3 trail positions
          if (this.trailPositions.length > 3) {
            this.trailPositions.shift();
          }
        }

        // Move phase for wave animation (horizontal movement)
        this.phase += this.phaseSpeed * this.direction;

        // Gentle vertical drift to simulate waves meeting
        this.y += this.verticalDirection * this.verticalSpeed;
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.opacity === 0 || this.age < this.delay) return;

        ctx.save();
        ctx.strokeStyle = this.color;
        ctx.lineWidth = config.lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw trail echoes if this wave has trails
        if (this.hasTrail && this.trailPositions.length > 0) {
          this.trailPositions.forEach((trail, index) => {
            const trailOpacity = trail.opacity * (index / this.trailPositions.length);
            ctx.globalAlpha = trailOpacity * 0.7;

            ctx.beginPath();
            for (let x = 0; x <= canvas!.width; x += 5) {
              // Add amplitude variation along the wave for more interesting shapes
              const amplitudeAtX = this.amplitude * (0.7 + Math.sin(x * 0.01 + this.phase * 0.5) * this.amplitudeVariation);
              const y = this.y + Math.sin(x * this.frequency + trail.phase) * amplitudeAtX;
              if (x === 0) {
                ctx.moveTo(x, y);
              } else {
                ctx.lineTo(x, y);
              }
            }
            ctx.stroke();
          });
        }

        // Draw main wave
        ctx.globalAlpha = this.opacity * 0.7;
        ctx.beginPath();
        for (let x = 0; x <= canvas!.width; x += 5) {
          // Add amplitude variation along the wave
          const amplitudeAtX = this.amplitude * (0.7 + Math.sin(x * 0.01 + this.phase * 0.5) * this.amplitudeVariation);
          const y = this.y + Math.sin(x * this.frequency + this.phase) * amplitudeAtX;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();

        // Draw complementary wave moving in opposite direction (for meeting effect)
        ctx.globalAlpha = this.opacity * 0.5;
        ctx.beginPath();
        for (let x = 0; x <= canvas!.width; x += 5) {
          const amplitudeAtX = this.amplitude * 0.6 * (0.7 + Math.sin(x * 0.015 + this.phase * 0.3) * this.amplitudeVariation);
          const y = this.y + Math.sin(x * this.frequency + this.phase + Math.PI) * amplitudeAtX;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();

        ctx.globalAlpha = 1;
        ctx.restore();
      }

      isAlive() {
        return this.age < (this.lifetime + this.delay);
      }
    }

    const flowerTypes = [
      'powderPuff'
    ];

    function createRandomFlower(delay: number = 0): Flower {
      // Small margin to avoid clipping at absolute edges
      const margin = 20;
      const x = margin + Math.random() * (canvas!.width - margin * 2);
      const y = margin + Math.random() * (canvas!.height - margin * 2);
      const type = flowerTypes[Math.floor(Math.random() * flowerTypes.length)];
      const scale = 0.5 + Math.random() * 1.5;
      return new Flower(x, y, type, scale, delay);
    }

    function createDistributedFlower(index: number, total: number, delay: number = 0): Flower {
      const cols = Math.ceil(Math.sqrt(total * (canvas!.width / canvas!.height)));
      const rows = Math.ceil(total / cols);
      const col = index % cols;
      const row = Math.floor(index / cols);
      const cellWidth = canvas!.width / cols;
      const cellHeight = canvas!.height / rows;
      const x = col * cellWidth + cellWidth * (0.05 + Math.random() * 0.9);
      const y = row * cellHeight + cellHeight * (0.05 + Math.random() * 0.9);
      const type = flowerTypes[Math.floor(Math.random() * flowerTypes.length)];
      const scale = 0.5 + Math.random() * 1.5;
      return new Flower(x, y, type, scale, delay);
    }

    function createRandomBubble(delay: number = 0): Bubble {
      // Random edge spawning
      const scale = 0.5 + Math.random() * 1.2;
      return new Bubble(0, 0, scale, delay); // Constructor will set position based on random edge
    }

    function createDistributedBubble(index: number, total: number, delay: number = 0): Bubble {
      const scale = 0.5 + Math.random() * 1.2;
      // Distribute bubbles among the 4 edges
      const edgeIndex = index % 4;
      const edges = ['top', 'right', 'bottom', 'left'];
      const edge = edges[edgeIndex];

      // Position along the chosen edge
      let x = 0, y = 0;
      const positionOnEdge = (Math.floor(index / 4) / Math.ceil(total / 4));

      switch (edge) {
        case 'top':
        case 'bottom':
          x = positionOnEdge * canvas!.width;
          break;
        case 'left':
        case 'right':
          y = positionOnEdge * canvas!.height;
          break;
      }

      return new Bubble(x, y, scale, delay, edge);
    }

    function createRandomButterfly(delay: number = 0): ButterflyElement {
      const margin = 20;
      const x = margin + Math.random() * (canvas!.width - margin * 2);
      const y = margin + Math.random() * (canvas!.height - margin * 2);
      const scale = 0.7 + Math.random() * 1.3;
      return new ButterflyElement(x, y, scale, delay);
    }

    function createDistributedButterfly(index: number, total: number, delay: number = 0): ButterflyElement {
      const cols = Math.ceil(Math.sqrt(total * (canvas!.width / canvas!.height)));
      const rows = Math.ceil(total / cols);
      const col = index % cols;
      const row = Math.floor(index / cols);
      const cellWidth = canvas!.width / cols;
      const cellHeight = canvas!.height / rows;
      const x = col * cellWidth + cellWidth * (0.05 + Math.random() * 0.9);
      const y = row * cellHeight + cellHeight * (0.05 + Math.random() * 0.9);
      const scale = 0.7 + Math.random() * 1.3;
      return new ButterflyElement(x, y, scale, delay);
    }

    function createRandomWave(delay: number = 0): Wave {
      const y = Math.random() * canvas!.height;
      return new Wave(y, delay);
    }

    function createDistributedWave(index: number, total: number, delay: number = 0): Wave {
      const y = (index / total) * canvas!.height;
      return new Wave(y, delay);
    }

    function getRandomTheme(excluding?: ThemeType): ThemeType {
      const themes: ThemeType[] = ['flowers', 'bubbles', 'waves'];
      const availableThemes = excluding ? themes.filter(t => t !== excluding) : themes;
      return availableThemes[Math.floor(Math.random() * availableThemes.length)];
    }

    function animate() {
      if (!animationRunning) return;

      const currentTime = Date.now();
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      // Clear background
      ctx!.fillStyle = config.backgroundColor;
      ctx!.fillRect(0, 0, canvas!.width, canvas!.height);

      // Theme cycling logic (skip when fixedTheme is set)
      themeTimer += deltaTime;

      if (!fixedTheme && themeTimer > config.themeChangeDuration && !isTransitioning) {
        // Start transition - begin fading in next theme
        isTransitioning = true;
        currentOpacity = 1;
        nextOpacity = 0;
        nextTheme = getRandomTheme(currentTheme);

        // Initialize next theme elements immediately
        nextFlowers = [];
        nextBubbles = [];
        nextWaves = [];

        for (let i = 0; i < config.maxElements; i++) {
          let delay;
          if (i < config.initialElementsNoDelay) {
            delay = 0;
          } else {
            delay = (i - config.initialElementsNoDelay) * config.spawnInterval;
          }

          switch (nextTheme) {
            case 'flowers':
              nextFlowers.push(createDistributedFlower(i, config.maxElements, delay));
              break;
            case 'bubbles':
              nextBubbles.push(createDistributedBubble(i, config.maxElements, delay));
              break;
            case 'waves':
              nextWaves.push(createDistributedWave(i, 15, delay));
              break;
          }
        }
      }

      if (isTransitioning) {
        // Cross-fade
        currentOpacity -= deltaTime / config.fadeDuration;
        nextOpacity += deltaTime / config.fadeDuration;

        if (currentOpacity <= 0) {
          // Transition complete
          currentOpacity = 1;
          nextOpacity = 0;

          if (nextTheme) {
            setCurrentTheme(nextTheme);
            // Move next theme to current
            flowers = nextFlowers;
            bubbles = nextBubbles;
            waves = nextWaves;

            // Clear next arrays
            nextFlowers = [];
            nextBubbles = [];
            nextWaves = [];

            nextTheme = null;
            isTransitioning = false;
            themeTimer = 0;
          }
        }
      }

      // Draw current theme with opacity
      ctx!.globalAlpha = currentOpacity;

      switch (currentTheme) {
        case 'flowers':
          for (let i = flowers.length - 1; i >= 0; i--) {
            flowers[i].update(deltaTime);
            flowers[i].draw(ctx!);

            if (!flowers[i].isAlive()) {
              flowers.splice(i, 1);
              if (!isTransitioning) flowers.push(createRandomFlower(500));
            }
          }
          if (flowers.length < 40 && !isTransitioning) {
            flowers.push(createRandomFlower(0));
          }
          break;

        case 'bubbles':
          for (let i = bubbles.length - 1; i >= 0; i--) {
            bubbles[i].update(deltaTime);
            bubbles[i].draw(ctx!);

            if (!bubbles[i].isAlive()) {
              bubbles.splice(i, 1);
              if (!isTransitioning) bubbles.push(createRandomBubble(200));
            }
          }
          if (bubbles.length < 50 && !isTransitioning) {
            bubbles.push(createRandomBubble(0));
          }
          break;

        case 'waves':
          for (let i = waves.length - 1; i >= 0; i--) {
            waves[i].update(deltaTime);
            waves[i].draw(ctx!);

            if (!waves[i].isAlive()) {
              waves.splice(i, 1);
              if (!isTransitioning) waves.push(createRandomWave(600));
            }
          }
          if (waves.length < 15 && !isTransitioning) {
            waves.push(createRandomWave(0));
          }
          break;
      }

      // Draw next theme if transitioning
      if (isTransitioning && nextTheme) {
        ctx!.globalAlpha = nextOpacity;

        switch (nextTheme) {
          case 'flowers':
            for (let i = nextFlowers.length - 1; i >= 0; i--) {
              nextFlowers[i].update(deltaTime);
              nextFlowers[i].draw(ctx!);
            }
            break;

          case 'bubbles':
            for (let i = nextBubbles.length - 1; i >= 0; i--) {
              nextBubbles[i].update(deltaTime);
              nextBubbles[i].draw(ctx!);
            }
            break;

          case 'waves':
            for (let i = nextWaves.length - 1; i >= 0; i--) {
              nextWaves[i].update(deltaTime);
              nextWaves[i].draw(ctx!);
            }
            break;
        }
      }

      ctx!.globalAlpha = 1;

      requestAnimationFrame(animate);
    }

    // Initialize with theme (fixedTheme or currentTheme, default flowers)
    const initTheme = fixedTheme ?? currentTheme;
    for (let i = 0; i < config.maxElements; i++) {
      let delay;
      if (i < config.initialElementsNoDelay) {
        delay = 0;
      } else {
        delay = (i - config.initialElementsNoDelay) * config.spawnInterval;
      }
      switch (initTheme) {
        case 'flowers':
          flowers.push(createDistributedFlower(i, config.maxElements, delay));
          break;
        case 'bubbles':
          bubbles.push(createDistributedBubble(i, config.maxElements, delay));
          break;
        case 'waves':
          waves.push(createDistributedWave(i, 15, delay));
          break;
      }
    }

    // Handle resize
    const handleResize = () => {
      resizeCanvas();
    };
    window.addEventListener('resize', handleResize);

    // Start animation
    animate();

    // Cleanup
    return () => {
      animationRunning = false;
      window.removeEventListener('resize', handleResize);
    };
  }, [currentTheme, fixedTheme]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 ${className}`}
      style={{ zIndex: 0 }}
    />
  );
}
